/**
 * What a form still needs before its primary button can send it (docs/ui-audit.md, triage item 1).
 * The primaries stay live: pressing one on an incomplete form says the first missing thing in the
 * peon's words and puts the focus on it, instead of a dim button that does nothing. Each check reads
 * the fields in the order they appear on the screen, so both clients name the same field with the
 * same line. `field` is the form's own name for the control the focus should go to.
 */
import { INVITE_CODE_LENGTH, isInviteCode, normalizeInviteCode } from "./members.ts";
import { normalizeName } from "./peons.ts";
import { NOTE_MAX_CHARS } from "./notes.ts";
import { UPGRADE_GOAL_MAX, UPGRADE_GOAL_MIN } from "./upgrades.ts";
import { WEEKLY_ENTRY_MAX } from "./weekly.ts";

export interface Missing {
  field: string;
  line: string;
}

export const MISSING_LINES = {
  taskTitle: "Say what needs doing first.",
  upgradeTitle: "Say what the house should get first.",
  photo: "Take the before photo first.",
  tier: "Not enough gold for that tier.",
  time: "Pick a time first.",
  timeShape: "Use a time like 09:00.",
  goal: "Set the goal first.",
  goalRange: `The goal is ${UPGRADE_GOAL_MIN} to ${UPGRADE_GOAL_MAX.toLocaleString("en-US")} wood.`,
  note: "Write the note first.",
  noteLong: "That note is too long.",
  weekly: "Write a line first.",
  weeklyLong: "That line is too long.",
  personName: "Type your name first.",
  personPassword: "Type your password first.",
  householdPassword: "Type the household password first.",
  code: "Type the invite code first.",
  codeShort: "An invite code has 8 characters.",
  codeBad: "That is not an invite code.",
  houseName: "Name the household first.",
  newPassword: "Pick a password first.",
  again: "Type the password again.",
  creationCode: "Type the creation code first.",
} as const;

const miss = (field: string, line: string): Missing => ({ field, line });

/** Add Task: the luxury tier (its banner sits above the form), the title, the before photo. */
export function taskMissing(f: { title: string; photoId: string | null; canAffordTier?: boolean }): Missing | null {
  if (f.canAffordTier === false) return miss("tier", MISSING_LINES.tier);
  if (!f.title.trim()) return miss("title", MISSING_LINES.taskTitle);
  if (!f.photoId) return miss("photo", MISSING_LINES.photo);
  return null;
}

/** A time as the server reads it (server/schedule.ts parseTime): H:MM or HH:MM, at most 23:59. */
export function isScheduleTime(t: string): boolean {
  const m = /^(\d{1,2}):(\d{2})$/.exec(t.trim());
  return !!m && Number(m[1]) <= 23 && Number(m[2]) <= 59;
}

/** New and Edit schedule: the title, the time ("At"), the before photo. */
export function scheduleMissing(f: { title: string; time: string; photoId: string | null }): Missing | null {
  if (!f.title.trim()) return miss("title", MISSING_LINES.taskTitle);
  if (!f.time.trim()) return miss("time", MISSING_LINES.time);
  if (!isScheduleTime(f.time)) return miss("time", MISSING_LINES.timeShape);
  if (!f.photoId) return miss("photo", MISSING_LINES.photo);
  return null;
}

/** New upgrade: the title, the goal (digits as typed), the before photo. */
export function upgradeMissing(f: { title: string; goalText: string; photoId: string | null }): Missing | null {
  if (!f.title.trim()) return miss("title", MISSING_LINES.upgradeTitle);
  const t = f.goalText.trim();
  if (!t) return miss("goal", MISSING_LINES.goal);
  const goal = /^\d+$/.test(t) ? Number(t) : NaN;
  if (!Number.isInteger(goal) || goal < UPGRADE_GOAL_MIN || goal > UPGRADE_GOAL_MAX) return miss("goal", MISSING_LINES.goalRange);
  if (!f.photoId) return miss("photo", MISSING_LINES.photo);
  return null;
}

/** The pinned-note composer: words, and not more than the limit. */
export function noteMissing(f: { draft: string; max?: number; /** A photo note (docs/notes.md "Photo notes"): the caption may be empty. */ photo?: boolean }): Missing | null {
  if (!f.draft.trim() && !f.photo) return miss("note", MISSING_LINES.note);
  if (Array.from(f.draft).length > (f.max ?? NOTE_MAX_CHARS)) return miss("note", MISSING_LINES.noteLong);
  return null;
}

/** The weekly scroll's composer (docs/weekly.md "Writing on it"): words, and not more than the limit. A kind's chip is always on. */
export function weeklyMissing(f: { draft: string }): Missing | null {
  if (!f.draft.trim()) return miss("text", MISSING_LINES.weekly);
  if (Array.from(f.draft.trim()).length > WEEKLY_ENTRY_MAX) return miss("text", MISSING_LINES.weeklyLong);
  return null;
}

export type GateFormName = "person" | "password" | "invite" | "create";

/**
 * The gate's four forms. Only what is empty (or an invite code not yet whole) is "missing"; the
 * create form's rules on what was typed (shared/members.ts) keep their own lines.
 */
export function gateMissing(
  form: GateFormName,
  f: { personName?: string; personPassword?: string; password?: string; code?: string; name?: string; newPassword?: string; again?: string; creationCode?: string },
  createNeedsCode = false,
): Missing | null {
  if (form === "person") {
    if (!normalizeName(f.personName ?? "")) return miss("personName", MISSING_LINES.personName);
    if (!f.personPassword) return miss("personPassword", MISSING_LINES.personPassword);
    return null;
  }
  if (form === "password") return f.password ? null : miss("password", MISSING_LINES.householdPassword);
  if (form === "invite") {
    const code = normalizeInviteCode(f.code ?? "");
    if (!code) return miss("code", MISSING_LINES.code);
    if (code.length !== INVITE_CODE_LENGTH) return miss("code", MISSING_LINES.codeShort);
    return isInviteCode(code) ? null : miss("code", MISSING_LINES.codeBad);
  }
  if (!(f.name ?? "").trim()) return miss("name", MISSING_LINES.houseName);
  if (!f.newPassword) return miss("newPassword", MISSING_LINES.newPassword);
  if (!f.again) return miss("again", MISSING_LINES.again);
  if (createNeedsCode && !(f.creationCode ?? "").trim()) return miss("creationCode", MISSING_LINES.creationCode);
  return null;
}
